<template>
    <div>
        <app-layout>
            <template v-slot:header>
                <Header :title="$t('reimbursement_form')"
                        :isFilter="false"/>
            </template>
            <template v-slot:header-mobile>
                <HeaderMobile :title="$t('reimbursement_form')"
                              :isFilter="false"/>
            </template>

            <div v-if="requesterInfo">
                <!-- Requester Info Section -->
                <div class="row data-form">
                    <div class="col-12">
                        <div class="mb-4">
                            <h6 class="text-uppercase mb-0">{{ $t('information').toUpper() }}</h6>
                        </div>
                    </div>
                    <div class="col-12 col-sm-6 col-md-6 col-lg-4 col-xl-3">
                        <b-form-group :label="$t('full_name')">{{ requesterInfo.full_name }}</b-form-group>
                    </div>
                    <div class="col-12 col-sm-6 col-md-6 col-lg-4 col-xl-3">
                        <b-form-group :label="$t('employee_id')">{{ requesterInfo.employee_id }}</b-form-group>
                    </div>
                    <div class="col-12 col-sm-6 col-md-6 col-lg-4 col-xl-3">
                        <b-form-group :label="$t('job_title')">{{ requesterInfo.job_title }}</b-form-group>
                    </div>
                    <div class="col-12 col-sm-6 col-md-6 col-lg-4 col-xl-3">
                        <b-form-group :label="$t('budgetary_department')">{{ requesterInfo.budgetary_department }}</b-form-group>
                    </div>
                </div>

                <hr>
                <div class="mb-4">
                    <h6 class="text-uppercase mb-0">{{ $t('application_information').toUpper() }}</h6>
                </div>

                <ValidationObserver ref="reimbursementForm">
                    <b-row>
                        <!-- Notes -->
                        <b-col cols="12">
                            <ValidationProvider name="requester_notes" rules="" v-slot="{errors}">
                                <b-form-group :label="$t('requester_notes')">
                                    <b-form-textarea
                                        v-model="formData.requester_notes"
                                        :class="errors[0] ? 'is-invalid' : ''"
                                        rows="3"
                                        maxlength="2000"
                                    />
                                    <b-form-invalid-feedback v-if="errors[0]" v-html="errors[0]"/>
                                </b-form-group>
                            </ValidationProvider>
                        </b-col>
                    </b-row>

                    <!-- Items Section -->
                    <hr>
                    <div class="mb-4">
                        <h6 class="text-uppercase mb-0">{{ $t('items').toUpper() }}</h6>
                    </div>

                    <div
                        v-for="(item, index) in formData.items"
                        :key="index"
                        class="mb-3 p-3 border rounded"
                    >
                        <div class="d-flex justify-content-between align-items-center mb-2">
                            <span class="badge badge-primary font-weight-bold">#{{ index + 1 }}</span>
                        </div>
                        <b-row>
                            <!-- Type of Expense -->
                            <b-col cols="12" md="3">
                                <ValidationProvider
                                    :name="'items.' + index + '.type_of_expense'"
                                    rules="required"
                                    v-slot="{errors}"
                                >
                                    <b-form-group :label="$t('type_of_expense') + ' *'">
                                        <b-form-input
                                            v-model="item.type_of_expense"
                                            :class="errors[0] ? 'is-invalid' : ''"
                                            maxlength="255"
                                        />
                                        <b-form-invalid-feedback v-if="errors[0]" v-html="errors[0]"/>
                                    </b-form-group>
                                </ValidationProvider>
                            </b-col>

                            <!-- Expense Category -->
                            <b-col cols="12" md="3">
                                <ValidationProvider
                                    :name="'items.' + index + '.expense_category'"
                                    rules="required"
                                    v-slot="{errors}"
                                >
                                    <b-form-group :label="$t('expense_category') + ' *'">
                                        <parameter-selectbox
                                            code="reimbursement_expense_categories"
                                            v-model="item.expense_category"
                                            :validate-error="errors[0] || ''"
                                        />
                                    </b-form-group>
                                </ValidationProvider>
                            </b-col>

                            <!-- Payment Method -->
                            <b-col cols="12" md="3">
                                <ValidationProvider
                                    :name="'items.' + index + '.payment_method'"
                                    rules="required"
                                    v-slot="{errors}"
                                >
                                    <b-form-group :label="$t('payment_method') + ' *'">
                                        <parameter-selectbox
                                            code="reimbursement_payment_methods"
                                            v-model="item.payment_method"
                                            :validate-error="errors[0] || ''"
                                        />
                                    </b-form-group>
                                </ValidationProvider>
                            </b-col>

                            <!-- Total -->
                            <b-col cols="12" md="2">
                                <ValidationProvider
                                    :name="'items.' + index + '.total'"
                                    rules="required"
                                    v-slot="{errors}"
                                >
                                    <b-form-group :label="$t('total') + ' *'">
                                        <b-form-input
                                            v-model="item.total"
                                            type="number"
                                            step="0.01"
                                            min="0.01"
                                            :class="errors[0] ? 'is-invalid' : ''"
                                        />
                                        <b-form-invalid-feedback v-if="errors[0]" v-html="errors[0]"/>
                                    </b-form-group>
                                </ValidationProvider>
                            </b-col>

                            <!-- Remove Button -->
                            <b-col cols="12" md="1" class="d-flex align-items-center mt-2">
                                <b-button
                                    variant="danger"
                                    size="sm"
                                    :disabled="formData.items.length === 1"
                                    @click="removeItem(index)"
                                >
                                    <i class="ri-delete-bin-line"></i>
                                </b-button>
                            </b-col>

                            <!-- Description -->
                            <b-col cols="12" class="mt-2">
                                <ValidationProvider
                                    :name="'items.' + index + '.description'"
                                    rules="max:1000"
                                    v-slot="{errors}"
                                >
                                    <b-form-group :label="$t('description')">
                                        <b-form-input
                                            v-model="item.description"
                                            :class="errors[0] ? 'is-invalid' : ''"
                                            maxlength="1000"
                                        />
                                        <b-form-invalid-feedback v-if="errors[0]" v-html="errors[0]"/>
                                    </b-form-group>
                                </ValidationProvider>
                            </b-col>
                        </b-row>
                    </div>

                    <b-button variant="outline-primary" size="sm" @click="addItem" class="mb-4">
                        <i class="ri-add-line"></i> {{ $t('add_item') }}
                    </b-button>

                    <!-- Evidence Files Section -->
                    <hr>
                    <b-row class="mb-4">
                        <b-col cols="12">
                            <ValidationProvider name="files" rules="required" v-slot="{errors, valid}">
                                <b-form-group :label="$t('evidence') + ' *'">
                                    <div v-if="requesterInfo && requesterInfo.evidence_note" class="alert alert-info py-2 px-3 mb-3 small d-flex align-items-center">
                                        <i class="ri-information-line mr-2"></i>
                                        <span>{{ requesterInfo.evidence_note }}</span>
                                    </div>
                                    <b-form-file
                                        v-model="formData.files"
                                        multiple
                                        accept=".jpg,.jpeg,.png,.pdf,.doc,.docx"
                                        :placeholder="$t('choose_files')"
                                        :drop-placeholder="$t('drop_files_here')"
                                        :state="errors[0] ? false : (valid ? true : null)"
                                    />
                                    <div class="text-muted small mt-1">
                                        {{ $t('allowed_file_types') }}: jpg, jpeg, png, pdf, doc, docx. (Max: 10MB)
                                    </div>
                                    <b-form-invalid-feedback v-if="errors[0]" v-html="errors[0]" class="d-block"/>
                                </b-form-group>
                            </ValidationProvider>
                        </b-col>
                    </b-row>
                </ValidationObserver>

                <div class="d-flex justify-content-center mt-3">
                    <b-form-group>
                        <b-button variant="primary" @click="save">{{ $t('save') }}</b-button>
                    </b-form-group>
                </div>
            </div>

            <!-- Not authorized / error message -->
            <div v-else>
                <div class="message-box" v-if="message && !showSuccessMessage">
                    <b-alert show variant="info" class="d-flex align-items-center">
                        <b-icon icon="info-circle"></b-icon>
                        <div class="alert-content ml-2">
                            <p class="mb-0 font-weight-bold">{{ $t('api.' + message) }}</p>
                        </div>
                    </b-alert>
                </div>
            </div>
        </app-layout>
    </div>
</template>

<script>
import AppLayout from '@/layouts/AppLayout';
import Header from '@/layouts/AppLayout/Header';
import HeaderMobile from '@/layouts/AppLayout/HeaderMobile';
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import ReimbursementFormService from '@/services/ReimbursementFormService';
import ParameterSelectbox from '@/components/interactive-fields/ParameterSelectbox';

export default {
    components: {
        AppLayout,
        Header,
        HeaderMobile,
        ValidationObserver,
        ValidationProvider,
        ParameterSelectbox,
    },
    data() {
        return {
            formData: {
                requester_notes: null,
                items: [
                    {
                        type_of_expense: null,
                        description: null,
                        expense_category: null,
                        payment_method: null,
                        total: null,
                    },
                ],
                files: [],
            },
            requesterInfo: null,
            showSuccessMessage: false,
            message: null,
        };
    },
    metaInfo() {
        return {
            title: this.$t('reimbursement_form'),
        };
    },
    methods: {
        async getFormData() {
            try {
                const response = await ReimbursementFormService.getFormData();
                this.requesterInfo = response.data.data;
            } catch (error) {
                this.message = error?.data?.message;
                this.showErrors(error);
            }
        },
        addItem() {
            this.formData.items.push({
                type_of_expense: null,
                description: null,
                expense_category: null,
                payment_method: null,
                total: null,
            });
        },
        removeItem(index) {
            this.formData.items.splice(index, 1);
        },
        async save() {
            if (!await this.$refs.reimbursementForm.validate()) return;
            try {
                const formData = new FormData();

                if (this.formData.requester_notes) {
                    formData.append('requester_notes', this.formData.requester_notes);
                }

                this.formData.items.forEach((item, index) => {
                    formData.append(`items[${index}][type_of_expense]`, item.type_of_expense || '');
                    if (item.description) {
                        formData.append(`items[${index}][description]`, item.description);
                    }
                    formData.append(`items[${index}][expense_category]`, item.expense_category || '');
                    formData.append(`items[${index}][payment_method]`, item.payment_method || '');
                    formData.append(`items[${index}][total]`, item.total || '');
                });

                if (this.formData.files && this.formData.files.length) {
                    this.formData.files.forEach(file => {
                        formData.append('files[]', file);
                    });
                }

                const config = {
                    headers: { 'Content-Type': 'multipart/form-data' },
                };

                const res = await ReimbursementFormService.store(formData, config);
                this.$toast.success(this.$t(res.data.message));
                this.$router.push('/my-staff-requests/1');
            } catch (error) {
                this.showErrors(error, this.$refs.reimbursementForm);
            }
        },
    },
    async created() {
        await this.getFormData();
    },
};
</script>
